import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PublicLayout } from '../components/layout/PublicLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Search, User, Phone, Gift, Ticket, History, XCircle, Clock3 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PassengerProfilePage = () => {
  const [searchParams] = useSearchParams();
  const { user, token } = useAuth();
  const initialPhone = searchParams.get('phone') || user?.phone || '';
  const initialBookingRef = searchParams.get('ref') || '';
  const [phone, setPhone] = useState(initialPhone);
  const [ticketNumber, setTicketNumber] = useState('');
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (initialBookingRef) {
      fetchProfile('', initialBookingRef);
    } else if (initialPhone) {
      fetchProfile(initialPhone);
    }
  }, [initialPhone, initialBookingRef]);

  const fetchProfile = async (phoneValue, bookingRefValue = '') => {
    if (!phoneValue?.trim() && !bookingRefValue?.trim()) {
      toast.error('Please enter a phone number or ticket number');
      return;
    }

    setLoading(true);
    try {
      const isOwnProfile = user?.role === 'passenger' && !bookingRefValue && phoneValue === user.phone;
      let response;
      if (isOwnProfile) {
        try {
          response = await axios.get(`${API_URL}/passengers/me`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          });
        } catch (error) {
          if (error.response?.status !== 404) throw error;
          response = await axios.get(`${API_URL}/passengers/profile`, { params: { phone: phoneValue } });
        }
      } else {
        response = await axios.get(`${API_URL}/passengers/profile`, {
          params: bookingRefValue ? { booking_ref: bookingRefValue } : { phone: phoneValue }
        });
      }
      setProfile(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Profile not found');
      setProfile(null);
    } finally {
      setLoading(false);
    }
  };

  const handleTicketLookup = async () => {
    const value = ticketNumber.trim();
    if (!value) {
      toast.error('Please enter a ticket number');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/tickets/lookup`, {
        params: { ticket_number: value }
      });
      const booking = response.data;
      setProfile({
        profile: {
          name: booking?.matched_passenger?.full_name || 'Passenger',
          phone: booking?.matched_passenger?.phone || '',
          email: booking?.matched_passenger?.email || '',
        },
        loyalty: {
          trips_completed: 0,
          tier: 'Bronze',
          next_free_trip: 7,
          is_eligible_for_free: false,
          total_spend_usd: 0,
        },
        history: [{
          booking_ref: booking.booking_ref,
          ticket_number: booking?.matched_ticket_number || value,
          origin: booking.origin || 'Harare',
          destination: booking.destination || 'Durban',
          departure_datetime: booking.departure_datetime,
          seat_numbers: booking.seat_numbers || [],
          status: booking.status,
          payment_status: booking.payment_status,
          total_amount: booking.total_amount,
          currency: booking.currency,
          created_at: booking.created_at,
          ticket_history: booking.ticket_history || [],
        }],
        booking_ref: booking.booking_ref,
      });
      setPhone(booking?.matched_passenger?.phone || '');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ticket not found');
      setProfile(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (bookingRef) => {
    if (!bookingRef || !window.confirm('Cancel this ticket and release the seat?')) return;
    setCancelling(true);
    try {
      await axios.post(`${API_URL}/bookings/ticket/${encodeURIComponent(bookingRef)}/cancel`, null, { params: { reason: 'Cancelled by passenger self-service' } });
      toast.success('Booking cancelled successfully');
      if (phone) {
        await fetchProfile(phone);
      } else {
        await handleTicketLookup();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to cancel booking');
    } finally {
      setCancelling(false);
    }
  };

  return (
    <PublicLayout>
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        <div className="text-center">
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900">My Passenger Dashboard</h1>
          <p className="text-slate-600 mt-2">Track your tickets, trip history, and loyalty status.</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Search className="h-5 w-5" /> Find My Booking</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-slate-600">Phone number</label>
                <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+263 77 123 4567" />
              </div>
              <div>
                <label className="text-sm text-slate-600">Ticket number</label>
                <Input value={ticketNumber} onChange={(e) => setTicketNumber(e.target.value.toUpperCase())} placeholder="PAN250101ABC123-1" />
              </div>
            </div>
            <div className="flex gap-3 flex-wrap">
              <Button className="btn-panashe" onClick={() => fetchProfile(phone)} disabled={loading}>
                {loading ? 'Loading...' : 'View My Profile'}
              </Button>
              <Button variant="outline" onClick={handleTicketLookup} disabled={loading}>
                Track by Ticket Number
              </Button>
            </div>
          </CardContent>
        </Card>

        {profile && (
          <>
            <div className="grid md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-3 text-slate-600"><User className="h-4 w-4" /> Profile</div>
                  <p className="font-bold text-lg">{profile.profile?.name || 'Passenger'}</p>
                  <p className="text-sm mt-2 flex items-center gap-2"><Phone className="h-4 w-4" /> {profile.profile?.phone || 'N/A'}</p>
                  <p className="text-sm mt-1">{profile.profile?.email || 'No email on file'}</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-3 text-slate-600"><Gift className="h-4 w-4" /> Loyalty</div>
                  <Badge className="mb-2">{profile.loyalty?.tier || 'Bronze'}</Badge>
                  <p className="text-2xl font-bold">{profile.loyalty?.trips_completed ?? 0}</p>
                  <p className="text-sm text-slate-600">Trips completed</p>
                  <p className="text-sm mt-2">{profile.loyalty?.next_free_trip ?? 7} trips to free trip</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-3 text-slate-600"><Ticket className="h-4 w-4" /> Latest Booking</div>
                  <p className="font-mono text-sm">{profile.booking_ref || profile.history?.[0]?.booking_ref || 'N/A'}</p>
                  <p className="text-sm mt-2">{profile.history?.[0]?.status || 'N/A'}</p>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><History className="h-5 w-5" /> Trip History</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {profile.history?.length ? profile.history.map((item, index) => (
                  <div key={`${item.booking_ref}-${index}`} className="border rounded-lg p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-slate-900">{item.origin || 'Harare'}</span>
                        <span className="text-slate-400">→</span>
                        <span className="font-bold text-slate-900">{item.destination || 'Durban'}</span>
                      </div>
                      <p className="font-mono text-xs mt-1">Ref: {item.booking_ref} | Ticket: {item.ticket_number || 'N/A'}</p>
                      <p className="text-sm text-slate-600 mt-1">{item.departure_datetime ? new Date(item.departure_datetime).toLocaleString() : 'N/A'}</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="outline">{item.status || 'confirmed'}</Badge>
                      <Badge variant="outline">Seats: {item.seat_numbers?.join(', ') || 'N/A'}</Badge>
                      <Button
                        variant="outline"
                        className="border-red-500 text-red-700 hover:bg-red-50"
                        onClick={() => handleCancel(item.booking_ref)}
                        disabled={cancelling || ['cancelled', 'completed', 'refunded'].includes(item.status)}
                      >
                        <XCircle className="h-4 w-4 mr-1" /> Cancel
                      </Button>
                    </div>
                  </div>
                )) : (
                  <div className="text-slate-500 py-4">No trip history found.</div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Clock3 className="h-5 w-5" /> Ticket Activity</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {profile.history?.flatMap((item) => (item.ticket_history || []).map((event) => ({ ...event, booking_ref: item.booking_ref }))).length ? (
                  profile.history.flatMap((item) => (item.ticket_history || []).map((event) => ({ ...event, booking_ref: item.booking_ref })))
                    .sort((left, right) => new Date(right.at) - new Date(left.at))
                    .map((event) => (
                      <div key={event.id} className="border-l-2 border-panashe-blue pl-4 py-1">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <p className="font-semibold capitalize">{event.action?.replaceAll('_', ' ')}</p>
                          <time className="text-xs text-slate-500">{event.at ? new Date(event.at).toLocaleString() : 'Time unavailable'}</time>
                        </div>
                        <p className="text-sm text-slate-600">{event.actor_name || 'System'}{event.actor_role ? ` · ${event.actor_role}` : ''} · {event.booking_ref}</p>
                        {event.details && Object.keys(event.details).length > 0 && (
                          <p className="text-xs text-slate-500 mt-1 break-words">{Object.entries(event.details).map(([key, value]) => `${key.replaceAll('_', ' ')}: ${Array.isArray(value) ? value.join(', ') : value}`).join(' · ')}</p>
                        )}
                      </div>
                    ))
                ) : (
                  <p className="text-sm text-slate-500">No ticket changes recorded yet.</p>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </PublicLayout>
  );
};

export default PassengerProfilePage;
